import { useEffect, useRef, useState } from 'react';
import { useCustomer } from '../contexts/CustomerContext';
import useCart from './useCart';

// Set (by CustomerCart's remove-coupon) when the customer explicitly removes a
// coupon from a cart; checked here so auto-apply NEVER re-attaches an offer the
// customer deliberately took off. Keyed per cart — a fresh cart starts clean.
export const offerRemovedKey = (cartId) => `offerRemoved:${cartId}`;

/**
 * Auto-applies a resolved promotional offer coupon to the customer's cart, so the
 * discount is real by the time they reach checkout without them entering a code.
 *
 * Mirrors OfferableCoupon's apply: set couponId, save, refresh context. Uses the
 * MAIN cart (customer.cart), which — unlike apptCart — persists to localStorage,
 * so there's no reload/bounce hazard.
 *
 * NEVER replaces a coupon the customer already has, and never re-applies to a
 * cart the customer explicitly removed a coupon from (localStorage marker).
 *
 * Returns { applied }: true once the apply attempt has SETTLED — coupon present
 * (this offer's or a pre-existing one), removal honored, or the save failed
 * (settled-without-discount is honest; a failed save also clears the retry
 * guard so a later mount can try again). The order-review screen gates its
 * first paint on this.
 */
export default function useApplyOfferToCart(offer) {
  const { customer, setCustomer } = useCustomer();
  const { data: cart } = useCart(customer?.cart?.id);
  const appliedRef = useRef(null);
  const [applied, setApplied] = useState(false);

  useEffect(() => {
    if (!offer || !cart) return;
    if (cart.couponId) {
      // This offer already on (round-tripped), or a different coupon the customer
      // chose — either way the cart is couponed; don't overwrite, report settled.
      setApplied(true);
      return;
    }
    if (localStorage.getItem(offerRemovedKey(cart.id))) {
      // The customer removed a coupon from this cart on purpose — their call stands.
      setApplied(true);
      return;
    }
    // Once-per-(cart, offer) — NOT once-per-offer. A new cart can replace the context
    // cart mid-session (access-code re-entry, post-purchase reset); keyed only on the
    // offer, the guard refused to apply to the replacement cart and the promo was
    // silently lost by checkout (2026-07-22 incident: coupon stranded on the old cart).
    const applyKey = `${cart.id}:${offer.id}`;
    if (appliedRef.current === applyKey) return;

    appliedRef.current = applyKey;
    cart.couponId = offer.id;
    cart.save()
      .then(() => {
        setApplied(true);
        setCustomer((prev) => ({ ...prev, cart }));
      })
      .catch((err) => {
        // Settle the gate (paint without the discount — honest) and clear the
        // guard so a later mount can retry; a transient failure must not
        // silently cost the customer the promo forever.
        console.error('useApplyOfferToCart: apply failed', err);
        appliedRef.current = null;
        cart.couponId = null;
        setApplied(true);
      });
  }, [offer, cart, setCustomer]);

  return { applied };
}
